/**
 * CityDooh Win-Card Generator
 *
 * Renders a 1080×1350 (4:5) shareable "You Won!" card to an offscreen
 * <canvas> and returns a PNG Blob. The design is a 1:1 recreation of
 * the CityDooh "Editorial Graffiti" poster reference:
 *
 *   - Cream paper background (#F7F4EC) with a subtle dot texture and
 *     scattered halftone dot clusters + doodle graphics (crescent,
 *     crown, star, lightning bolt, squiggle) around the edges.
 *   - Featured campaign image at the top, black outline + yellow
 *     offset-shadow (sticker style), rounded corners.
 *   - Left-aligned vendor row: square vendor logo (with black bg /
 *     yellow offset) + vendor name + campaign name.
 *   - Angled yellow "YOU WON" tape banner with small black spark
 *     ticks on either side.
 *   - Enormous Anton prize name sitting on top of a yellow torn
 *     highlighter polygon (paint-stroke behind text).
 *   - White rounded coupon pill: thin black border + yellow offset
 *     shadow, "YOUR COUPON CODE" small label + coupon in Anton.
 *   - "● Powered by CityDooh" footer with yellow bullet.
 *
 * Anton is the primary display font. Inter is used for supporting
 * labels. Both are loaded globally via `/app/frontend/public/index.html`.
 *
 * The exported blob is high-fidelity (PNG, quality 0.98) and preserves
 * pixel-sharp typography. The card is rendered at native 1080×1350 —
 * no browser screenshotting, no DOM cloning.
 */

/* ── Canvas + colour tokens ─────────────────────────────────────── */
const CARD_W = 1080;
const CARD_H = 1350;

const PAPER = "#F7F4EC";          // warm cream background
const INK = "#0A0A0A";             // main text + outlines
const YELLOW = "#F5A800";          // CityDooh brand yellow
const YELLOW_HL = "#F5A800";       // yellow highlighter (same tone)
const WHITE = "#FFFFFF";
const GREY = "#4A4A4A";
const GREY_SOFT = "#6B6B6B";
const NAVY = "#0F1636";            // fallback logo backdrop (matches ref)

/* ── Fonts ──────────────────────────────────────────────────────── */
// Loaded globally via <link> in public/index.html:
//   Anton, Inter (400..700), Figtree, Caveat.
const DISPLAY_FONT = '"Anton", "Impact", "Arial Black", sans-serif';
const BODY_FONT = '"Inter", "Poppins", system-ui, sans-serif';

/* ─────────────────────────────────────────────────────────────────
 * Font loading — canvas won't wait for webfonts automatically so we
 * explicitly kick off loads and await `document.fonts.ready`. If any
 * of them time out (~1s) we fall back to the OS-installed fallback.
 * ───────────────────────────────────────────────────────────────── */
async function ensureFontsReady() {
  if (typeof document === "undefined" || !document.fonts) return;
  try {
    await Promise.race([
      Promise.all([
        document.fonts.load("700 120px Anton"),
        document.fonts.load("400 24px Inter"),
        document.fonts.load("700 40px Inter"),
      ]).then(() => document.fonts.ready),
      new Promise((r) => setTimeout(r, 1200)),
    ]);
  } catch (err) {
    /* fonts.load can reject in older browsers — safe to ignore. Log
     * at debug level for observability without polluting console. */
    if (typeof console !== "undefined") {
      console.debug("[winCard] font preload failed; using fallback", err);
    }
  }
}

/* ─────────────────────────────────────────────────────────────────
 * Small drawing helpers
 * ───────────────────────────────────────────────────────────────── */

function loadImage(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
    setTimeout(() => resolve(null), 4500);
  });
}

function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, Math.min(w, h) / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/* Slugify helper for the download filename. */
function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48) || "campaign";
}

/* ─────────────────────────────────────────────────────────────────
 * Paper texture — soft radial dot pattern.
 * ───────────────────────────────────────────────────────────────── */
function drawPaperTexture(ctx) {
  ctx.save();
  ctx.fillStyle = "rgba(0,0,0,0.04)";
  for (let x = 0; x < CARD_W; x += 22) {
    for (let y = 0; y < CARD_H; y += 22) {
      ctx.beginPath();
      ctx.arc(x, y, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.fillStyle = "rgba(0,0,0,0.022)";
  for (let x = 11; x < CARD_W; x += 13) {
    for (let y = 11; y < CARD_H; y += 13) {
      ctx.beginPath();
      ctx.arc(x, y, 0.7, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

/* Dense halftone cluster — dots that fade from center. */
function drawHalftoneCluster(ctx, cx, cy, radius, { color = INK, intensity = 0.35 } = {}) {
  ctx.save();
  const step = 12;
  for (let x = cx - radius; x <= cx + radius; x += step) {
    for (let y = cy - radius; y <= cy + radius; y += step) {
      const dx = x - cx;
      const dy = y - cy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d > radius) continue;
      const alpha = Math.max(0, 1 - d / radius) * intensity;
      const r = 1.6 * (1 - d / radius) + 0.8;
      ctx.fillStyle = color.replace(/^#/, "");
      // Convert hex to rgba
      const hex = color.replace("#", "");
      const [rr, gg, bb] = [0, 2, 4].map((i) =>
        parseInt(hex.substr(i, 2), 16),
      );
      ctx.fillStyle = `rgba(${rr},${gg},${bb},${alpha.toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

/* ─────────────────────────────────────────────────────────────────
 * Doodle primitives — inline SVG paths translated to Path2D.
 * All doodles are centered on (cx, cy) then rotated + scaled.
 * ───────────────────────────────────────────────────────────────── */

function drawDoodle(ctx, { pathD, viewW, viewH, cx, cy, size, rotate = 0, fill = null, stroke = null, strokeWidth = 4 }) {
  const scale = size / Math.max(viewW, viewH);
  const w = viewW * scale;
  const h = viewH * scale;
  ctx.save();
  ctx.translate(cx, cy);
  if (rotate) ctx.rotate((rotate * Math.PI) / 180);
  ctx.translate(-w / 2, -h / 2);
  ctx.scale(scale, scale);
  const p = new Path2D(pathD);
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill(p);
  }
  if (stroke) {
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.lineWidth = strokeWidth / scale;
    ctx.strokeStyle = stroke;
    ctx.stroke(p);
  }
  ctx.restore();
}

const DOODLE = {
  // Crescent / comma shape — top-left decorator.
  crescent: (ctx, opts) =>
    drawDoodle(ctx, {
      pathD: "M20 6 C 8 12, 4 30, 14 40 C 6 30, 12 12, 24 10 Z",
      viewW: 40,
      viewH: 46,
      strokeWidth: 5,
      ...opts,
    }),

  // 5-point star outline (hand-drawn).
  starOutline: (ctx, opts) =>
    drawDoodle(ctx, {
      pathD:
        "M24 4 L29 18 L44 19 L32 29 L36 44 L24 36 L12 44 L16 29 L4 19 L19 18 Z",
      viewW: 48,
      viewH: 48,
      strokeWidth: 4,
      ...opts,
    }),

  // Chunky graffiti-style crown.
  crown: (ctx, opts) =>
    drawDoodle(ctx, {
      pathD: "M6 38 L10 14 L22 28 L32 8 L42 28 L54 14 L58 38 Z",
      viewW: 64,
      viewH: 48,
      strokeWidth: 5,
      ...opts,
    }),

  // Lightning bolt.
  bolt: (ctx, opts) =>
    drawDoodle(ctx, {
      pathD: "M18 2 L4 26 L14 26 L10 46 L28 22 L18 22 Z",
      viewW: 32,
      viewH: 48,
      strokeWidth: 4,
      ...opts,
    }),

  // Squiggle underline.
  squiggle: (ctx, opts) =>
    drawDoodle(ctx, {
      pathD: "M2 14 Q 18 0, 35 12 T 70 14 T 105 12 T 140 14 T 175 12 T 198 14",
      viewW: 200,
      viewH: 24,
      strokeWidth: 5,
      ...opts,
    }),

  // "Spark" tick — three short radial lines from a central point.
  // Not path-based; drawn directly with strokes below.
};

/* Spark ticks (used to flank YOU WON banner). */
function drawSparkTicks(ctx, cx, cy, size = 22, { color = INK, lineWidth = 4, flip = false } = {}) {
  ctx.save();
  ctx.translate(cx, cy);
  if (flip) ctx.scale(-1, 1);
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  // Three short strokes fanning outward to the LEFT.
  const strokes = [
    [0, -size * 0.9, -size, -size * 0.9],
    [0, 0, -size * 1.1, 0],
    [0, size * 0.9, -size, size * 0.9],
  ];
  strokes.forEach(([x1, y1, x2, y2]) => {
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  });
  ctx.restore();
}

/* ─────────────────────────────────────────────────────────────────
 * Yellow torn "highlighter" polygon — the ragged paint stroke that
 * sits behind the prize name in the reference. Rendered as a wide
 * paint stroke that extends beyond the text on all sides with jagged
 * torn edges and a subtle tilt.
 * ───────────────────────────────────────────────────────────────── */
function drawHighlighter(ctx, cx, cy, w, h, { rotate = -1.5, seed = 7 } = {}) {
  // Deterministic pseudo-random so the shape is stable per prize.
  const rand = (i) => {
    const s = Math.sin(i * 12.9898 + seed * 78.233) * 43758.5453;
    return s - Math.floor(s);
  };
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((rotate * Math.PI) / 180);

  const left = -w / 2;
  const right = w / 2;
  const top = -h / 2;
  const bottom = h / 2;

  ctx.fillStyle = YELLOW_HL;

  // Main body — jagged rectangle with rough top + bottom edges.
  const steps = 24;
  ctx.beginPath();
  // Top edge (left → right)
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const x = left + t * w;
    const jitter = (rand(i) - 0.5) * h * 0.09;
    const y = top + jitter;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  // Right torn edge — spiky triangles fanning outward.
  ctx.lineTo(right + h * 0.18, top + h * 0.15);
  ctx.lineTo(right + h * 0.05, top + h * 0.35);
  ctx.lineTo(right + h * 0.28, top + h * 0.55);
  ctx.lineTo(right + h * 0.1, top + h * 0.75);
  ctx.lineTo(right + h * 0.22, bottom - h * 0.05);
  // Bottom edge (right → left)
  for (let i = steps; i >= 0; i -= 1) {
    const t = i / steps;
    const x = left + t * w;
    const jitter = (rand(i + 99) - 0.5) * h * 0.09;
    const y = bottom + jitter;
    ctx.lineTo(x, y);
  }
  // Left torn edge — spiky triangles fanning outward.
  ctx.lineTo(left - h * 0.22, bottom - h * 0.15);
  ctx.lineTo(left - h * 0.05, bottom - h * 0.4);
  ctx.lineTo(left - h * 0.28, bottom - h * 0.55);
  ctx.lineTo(left - h * 0.1, top + h * 0.3);
  ctx.lineTo(left - h * 0.18, top + h * 0.1);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}

/* ─────────────────────────────────────────────────────────────────
 * Dynamic-fit text — measures then shrinks font-size until the text
 * fits inside (maxWidth × maxHeight). Wraps at whitespace by default,
 * or at hyphens when `breakOnHyphen` is true (for coupon codes).
 *
 * Returns { lines, fontSize, lineHeight }.
 * ───────────────────────────────────────────────────────────────── */
function fitText(
  ctx,
  text,
  {
    fontFamily,
    fontWeight = 700,
    startSize,
    minSize,
    maxWidth,
    maxHeight,
    maxLines = 3,
    lineHeightRatio = 1.0,
    breakOnHyphen = false,
    uppercase = false,
  },
) {
  let str = String(text || "").trim();
  if (uppercase) str = str.toUpperCase();
  if (!str) return { lines: [""], fontSize: startSize, lineHeight: startSize };

  const wrap = (font, tokens) => {
    ctx.font = font;
    const lines = [];
    let current = "";
    for (let i = 0; i < tokens.length; i += 1) {
      const tok = tokens[i];
      const attempt = current ? `${current}${breakOnHyphen ? "" : " "}${tok}` : tok;
      if (ctx.measureText(attempt).width <= maxWidth) {
        current = attempt;
      } else {
        if (current) lines.push(current);
        current = tok;
        if (lines.length >= maxLines) {
          // overflow: bail with residual concatenated
          current = tokens.slice(i).join(breakOnHyphen ? "" : " ");
          break;
        }
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  // Tokenise. For hyphen breaking we keep the hyphen ATTACHED to the
  // segment on its LEFT so lines break naturally (e.g. "CTD-WPJ7TH"
  // → ["CTD-", "WPJ7TH"]).
  const tokens = breakOnHyphen
    ? str.split(/(?<=-)/).filter(Boolean)
    : str.split(/\s+/).filter(Boolean);

  let size = startSize;
  let lines = [];
  while (size >= minSize) {
    const font = `${fontWeight} ${size}px ${fontFamily}`;
    ctx.font = font;
    // Single-line check first — if it fits, use it.
    const singleWidth = ctx.measureText(str).width;
    if (singleWidth <= maxWidth) {
      lines = [str];
      const lh = size * lineHeightRatio;
      if (lh <= maxHeight) return { lines, fontSize: size, lineHeight: lh };
    }
    // Wrap.
    lines = wrap(font, tokens);
    // Check every line width — some may still overflow after wrap.
    const widest = Math.max(...lines.map((l) => ctx.measureText(l).width));
    const totalHeight = lines.length * size * lineHeightRatio;
    if (widest <= maxWidth && totalHeight <= maxHeight && lines.length <= maxLines) {
      return { lines, fontSize: size, lineHeight: size * lineHeightRatio };
    }
    size -= 2;
  }

  // Final fallback — hard truncate the last line with ellipsis.
  ctx.font = `${fontWeight} ${minSize}px ${fontFamily}`;
  lines = wrap(`${fontWeight} ${minSize}px ${fontFamily}`, tokens).slice(0, maxLines);
  if (lines.length) {
    let last = lines[lines.length - 1];
    while (ctx.measureText(last + "…").width > maxWidth && last.length > 1) {
      last = last.slice(0, -1);
    }
    lines[lines.length - 1] = last + "…";
  }
  return {
    lines: lines.length ? lines : [str.slice(0, 12) + "…"],
    fontSize: minSize,
    lineHeight: minSize * lineHeightRatio,
  };
}

/* Fallback vendor logo — black rounded square with the vendor's first
 * initial in yellow Anton. */
function drawFallbackLogo(ctx, vendor, x, y, size) {
  const initial = String(vendor || "?").trim().charAt(0).toUpperCase() || "?";
  // Yellow offset shadow behind.
  ctx.save();
  ctx.fillStyle = YELLOW;
  roundRect(ctx, x + 8, y + 8, size, size, 16);
  ctx.fill();
  // Navy/black body.
  ctx.fillStyle = NAVY;
  roundRect(ctx, x, y, size, size, 16);
  ctx.fill();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 3;
  ctx.stroke();
  // Initial.
  ctx.fillStyle = YELLOW;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `700 ${Math.round(size * 0.62)}px ${DISPLAY_FONT}`;
  ctx.fillText(initial, x + size / 2, y + size / 2 + size * 0.02);
  ctx.restore();
}

/* ─────────────────────────────────────────────────────────────────
 * Render the win-card.
 * Returns { canvas, blob }.
 * ───────────────────────────────────────────────────────────────── */
export async function renderWinCard({
  prizeLabel,
  couponCode,
  campaignName,
  vendorBusiness,
  logoUrl,
  thumbnailUrl,
  // Accepted for backward-compat with existing PrizeRevealModal call site
  // — the brand palette is now driven by design tokens, not per-campaign.
  primaryColor: _primaryColor,
} = {}) {
  await ensureFontsReady();

  const canvas = document.createElement("canvas");
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext("2d");

  /* ── Background ────────────────────────────────────────────────── */
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  drawPaperTexture(ctx);

  /* ── Corner + edge doodles (drawn first so content sits above) ─ */
  // Top-left crescent + small star outline.
  DOODLE.crescent(ctx, { cx: 68, cy: 90, size: 72, rotate: -18, fill: INK });
  DOODLE.starOutline(ctx, { cx: 110, cy: 210, size: 58, rotate: -10, stroke: INK, strokeWidth: 4 });

  // Top-right yellow crown + black bolt.
  DOODLE.crown(ctx, { cx: CARD_W - 110, cy: 96, size: 78, rotate: 12, stroke: YELLOW, strokeWidth: 6 });
  DOODLE.bolt(ctx, { cx: CARD_W - 68, cy: 260, size: 62, rotate: 14, fill: INK });

  // Mid-left halftone cluster.
  drawHalftoneCluster(ctx, 40, 650, 90, { color: INK, intensity: 0.42 });
  // Mid-right smaller halftone cluster.
  drawHalftoneCluster(ctx, CARD_W - 46, 780, 70, { color: INK, intensity: 0.32 });

  // Bottom-left squiggle + halftone.
  DOODLE.squiggle(ctx, {
    cx: 110,
    cy: CARD_H - 92,
    size: 170,
    rotate: -6,
    stroke: INK,
    strokeWidth: 5,
  });
  drawHalftoneCluster(ctx, 60, CARD_H - 180, 90, { color: INK, intensity: 0.35 });

  // Bottom-right yellow crescent + small tick.
  DOODLE.crescent(ctx, {
    cx: CARD_W - 78,
    cy: CARD_H - 108,
    size: 72,
    rotate: 168,
    fill: YELLOW,
  });

  /* ── 1. Featured image ─────────────────────────────────────────── */
  const imgX = 92;
  const imgW = CARD_W - imgX * 2;   // 896
  const imgH = 540;                  // hero band height
  const imgY = 66;
  const imgR = 28;
  const thumb = thumbnailUrl ? await loadImage(thumbnailUrl) : null;

  // Yellow offset shadow.
  ctx.save();
  ctx.fillStyle = YELLOW;
  roundRect(ctx, imgX + 12, imgY + 12, imgW, imgH, imgR);
  ctx.fill();
  ctx.restore();

  // Image plate (dark placeholder if no thumbnail).
  ctx.save();
  ctx.fillStyle = NAVY;
  roundRect(ctx, imgX, imgY, imgW, imgH, imgR);
  ctx.fill();
  // Clip + draw.
  roundRect(ctx, imgX, imgY, imgW, imgH, imgR);
  ctx.clip();
  if (thumb) {
    // object-fit: cover
    const scale = Math.max(imgW / thumb.width, imgH / thumb.height);
    const dw = thumb.width * scale;
    const dh = thumb.height * scale;
    const dx = imgX + (imgW - dw) / 2;
    const dy = imgY + (imgH - dh) / 2;
    ctx.drawImage(thumb, dx, dy, dw, dh);
  } else {
    // Placeholder: campaign name centered on navy.
    ctx.fillStyle = "rgba(255,255,255,0.14)";
    ctx.fillRect(imgX, imgY, imgW, imgH);
    ctx.fillStyle = "rgba(255,255,255,0.75)";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `700 68px ${DISPLAY_FONT}`;
    const heroTitle = (campaignName || vendorBusiness || "CityDooh").toUpperCase();
    ctx.fillText(heroTitle.slice(0, 22), imgX + imgW / 2, imgY + imgH / 2);
  }
  ctx.restore();

  // Black outline on top.
  ctx.save();
  ctx.strokeStyle = INK;
  ctx.lineWidth = 4;
  roundRect(ctx, imgX, imgY, imgW, imgH, imgR);
  ctx.stroke();
  ctx.restore();

  /* ── 2. Vendor row (LEFT-aligned) ──────────────────────────────── */
  const vendorTop = imgY + imgH + 44;   // ~650
  const logoSize = 118;
  const logoX = imgX;
  const logoY = vendorTop;

  const logo = logoUrl ? await loadImage(logoUrl) : null;
  if (logo) {
    // Yellow offset shadow.
    ctx.save();
    ctx.fillStyle = YELLOW;
    roundRect(ctx, logoX + 8, logoY + 8, logoSize, logoSize, 16);
    ctx.fill();
    ctx.restore();
    // Navy backdrop so any logo shape reads well.
    ctx.save();
    ctx.fillStyle = NAVY;
    roundRect(ctx, logoX, logoY, logoSize, logoSize, 16);
    ctx.fill();
    // Clip + draw (contain).
    roundRect(ctx, logoX, logoY, logoSize, logoSize, 16);
    ctx.clip();
    const pad = 10;
    const availW = logoSize - pad * 2;
    const availH = logoSize - pad * 2;
    const scale = Math.min(availW / logo.width, availH / logo.height);
    const dw = logo.width * scale;
    const dh = logo.height * scale;
    ctx.drawImage(logo, logoX + (logoSize - dw) / 2, logoY + (logoSize - dh) / 2, dw, dh);
    ctx.restore();
    // Outline.
    ctx.save();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 3;
    roundRect(ctx, logoX, logoY, logoSize, logoSize, 16);
    ctx.stroke();
    ctx.restore();
  } else {
    drawFallbackLogo(ctx, vendorBusiness, logoX, logoY, logoSize);
  }

  // Vendor text (right of logo).
  const textX = logoX + logoSize + 26;
  const textMaxW = CARD_W - textX - imgX;   // right padding matches left

  // Business name — bold, up to 2 lines.
  const businessFit = fitText(ctx, vendorBusiness || "", {
    fontFamily: BODY_FONT,
    fontWeight: 700,
    startSize: 40,
    minSize: 24,
    maxWidth: textMaxW,
    maxHeight: 100,
    maxLines: 2,
    lineHeightRatio: 1.1,
  });
  ctx.save();
  ctx.fillStyle = INK;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.font = `700 ${businessFit.fontSize}px ${BODY_FONT}`;
  let bnY = logoY + 8;
  businessFit.lines.forEach((line) => {
    ctx.fillText(line, textX, bnY);
    bnY += businessFit.lineHeight;
  });
  const bnBottom = bnY;
  ctx.restore();

  // Campaign name — regular, up to 2 lines, smaller.
  if (campaignName) {
    const campFit = fitText(ctx, campaignName, {
      fontFamily: BODY_FONT,
      fontWeight: 500,
      startSize: 26,
      minSize: 18,
      maxWidth: textMaxW,
      maxHeight: 74,
      maxLines: 2,
      lineHeightRatio: 1.2,
    });
    ctx.save();
    ctx.fillStyle = GREY;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.font = `500 ${campFit.fontSize}px ${BODY_FONT}`;
    let cnY = bnBottom + 8;
    campFit.lines.forEach((line) => {
      ctx.fillText(line, textX, cnY);
      cnY += campFit.lineHeight;
    });
    ctx.restore();
  }

  /* ── 3. "YOU WON" yellow tape ──────────────────────────────────── */
  const tapeCy = logoY + logoSize + 90;  // sits below vendor block
  const tapeW = 340;
  const tapeH = 80;
  ctx.save();
  ctx.translate(CARD_W / 2, tapeCy);
  ctx.rotate((-2 * Math.PI) / 180);
  // Body
  ctx.fillStyle = YELLOW;
  roundRect(ctx, -tapeW / 2, -tapeH / 2, tapeW, tapeH, 6);
  ctx.fill();
  // Text
  ctx.fillStyle = INK;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `400 50px ${DISPLAY_FONT}`;
  ctx.fillText("YOU WON", 0, 4);
  ctx.restore();

  // Flanking spark ticks — larger + closer to tape.
  drawSparkTicks(ctx, CARD_W / 2 - tapeW / 2 - 28, tapeCy, 26, { color: INK, lineWidth: 5 });
  drawSparkTicks(ctx, CARD_W / 2 + tapeW / 2 + 28, tapeCy, 26, { color: INK, lineWidth: 5, flip: true });

  /* ── 4. Prize name (HUGE Anton on yellow highlighter) ─────────── */
  // The coupon pill has a FIXED Y so the layout is stable regardless
  // of prize length. The prize block gets whatever vertical room
  // remains between the YOU WON tape and the coupon box.
  const footerCy = CARD_H - 46;
  const couponBoxH = 148;
  const couponBoxY = 1130;   // fixed → prize never overlaps coupon
  const couponBoxX = imgX;
  const couponBoxW = imgW;

  const prizeTop = tapeCy + tapeH / 2 + 22;
  const prizeMaxH = couponBoxY - 32 - prizeTop;  // ~230px

  const prizeMaxW = CARD_W - imgX * 2 - 20;
  const prizeFit = fitText(ctx, prizeLabel || "A PRIZE", {
    fontFamily: DISPLAY_FONT,
    fontWeight: 400,          // Anton has a single 400 weight
    startSize: 150,
    minSize: 42,
    maxWidth: prizeMaxW,
    maxHeight: prizeMaxH,
    maxLines: 3,
    lineHeightRatio: 0.94,
    uppercase: true,
  });

  const prizeBlockH = prizeFit.lines.length * prizeFit.lineHeight;
  // Vertically center the prize block within the available area.
  const prizeAreaCy = prizeTop + prizeMaxH / 2;
  const prizeBlockTop = prizeAreaCy - prizeBlockH / 2;

  // Yellow torn highlighter behind the prize block. Wide overhang so
  // the paint stroke reads as bold graffiti (matching the reference).
  ctx.font = `400 ${prizeFit.fontSize}px ${DISPLAY_FONT}`;
  const widest = Math.max(
    ...prizeFit.lines.map((l) => ctx.measureText(l).width),
  );
  // Highlighter extends 80px beyond widest line on each side (clamped
  // so torn tips don't push past the card padding).
  const hlW = Math.min(CARD_W - imgX * 2 + 30, widest + 160);
  const hlH = prizeBlockH + 40;
  drawHighlighter(ctx, CARD_W / 2, prizeAreaCy, hlW, hlH, {
    rotate: -1.5,
    seed: (prizeLabel || "").length + 3,
  });

  // Text on top.
  ctx.save();
  ctx.fillStyle = INK;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.font = `400 ${prizeFit.fontSize}px ${DISPLAY_FONT}`;
  let py = prizeBlockTop;
  prizeFit.lines.forEach((line) => {
    ctx.fillText(line, CARD_W / 2, py);
    py += prizeFit.lineHeight;
  });
  ctx.restore();

  /* ── 5. Coupon pill ────────────────────────────────────────────── */
  if (couponCode) {
    // Yellow offset shadow.
    ctx.save();
    ctx.fillStyle = YELLOW;
    roundRect(ctx, couponBoxX + 10, couponBoxY + 10, couponBoxW, couponBoxH, 40);
    ctx.fill();
    ctx.restore();
    // White body.
    ctx.save();
    ctx.fillStyle = WHITE;
    roundRect(ctx, couponBoxX, couponBoxY, couponBoxW, couponBoxH, 40);
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.restore();

    // "YOUR COUPON CODE" label.
    ctx.save();
    ctx.fillStyle = GREY_SOFT;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.font = `600 22px ${BODY_FONT}`;
    ctx.letterSpacing = "0.1em"; // ignored on older canvases but a hint
    ctx.fillText("YOUR COUPON CODE", CARD_W / 2, couponBoxY + 22);
    ctx.restore();

    // Coupon code — Anton, dynamically fit.
    const codeMaxW = couponBoxW - 60;
    const codeMaxH = couponBoxH - 66;
    const codeFit = fitText(ctx, couponCode, {
      fontFamily: DISPLAY_FONT,
      fontWeight: 400,
      startSize: 88,
      minSize: 32,
      maxWidth: codeMaxW,
      maxHeight: codeMaxH,
      maxLines: 2,
      lineHeightRatio: 0.98,
      breakOnHyphen: true,
      uppercase: true,
    });
    ctx.save();
    ctx.fillStyle = INK;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `400 ${codeFit.fontSize}px ${DISPLAY_FONT}`;
    const codeBlockH = codeFit.lines.length * codeFit.lineHeight;
    const codeStartY = couponBoxY + 66 + (codeMaxH - codeBlockH) / 2 + codeFit.lineHeight / 2;
    let cy = codeStartY;
    codeFit.lines.forEach((line) => {
      ctx.fillText(line, CARD_W / 2, cy);
      cy += codeFit.lineHeight;
    });
    ctx.restore();
  }

  /* ── 6. Footer: ● Powered by CityDooh ─────────────────────────── */
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const footerText = "Powered by CityDooh";
  ctx.font = `500 22px ${BODY_FONT}`;
  const tw = ctx.measureText(footerText).width;
  const dotR = 6;
  const gap = 12;
  const totalW = dotR * 2 + gap + tw;
  const startX = (CARD_W - totalW) / 2;
  // Yellow bullet.
  ctx.fillStyle = YELLOW;
  ctx.beginPath();
  ctx.arc(startX + dotR, footerCy, dotR, 0, Math.PI * 2);
  ctx.fill();
  // Text.
  ctx.fillStyle = GREY;
  ctx.textAlign = "left";
  ctx.fillText(footerText, startX + dotR * 2 + gap, footerCy + 1);
  ctx.restore();

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/png", 0.98),
  );
  return { canvas, blob };
}

/* ─────────────────────────────────────────────────────────────────
 * Download helper
 * ───────────────────────────────────────────────────────────────── */
export function downloadBlob(blob, filename) {
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "citydooh-win.png";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

/* Filename builder matching the spec: citydooh-{slug}-{code}.png */
export function buildCardFilename({ campaignName, couponCode } = {}) {
  const c = slugify(campaignName || "campaign");
  const k = slugify(couponCode || "win");
  return `citydooh-${c}-${k}.png`;
}

/* ─────────────────────────────────────────────────────────────────
 * Web Share API helper
 * Returns "shared" | "unsupported" | "cancelled" | "failed".
 * ───────────────────────────────────────────────────────────────── */
export async function shareBlobAsFile(blob, filename, { text, url } = {}) {
  if (!blob) return "unsupported";
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    return "unsupported";
  }
  try {
    const file = new File([blob], filename, { type: blob.type || "image/png" });
    if (
      typeof navigator.canShare === "function" &&
      !navigator.canShare({ files: [file] })
    ) {
      return "unsupported";
    }
    await navigator.share({ files: [file], text, url });
    return "shared";
  } catch (err) {
    if (err && (err.name === "AbortError" || err.code === 20)) return "cancelled";
    return "failed";
  }
}
